import { createContext, useContext, useEffect, useId, useLayoutEffect, useMemo, useSyncExternalStore } from "react";

type Snapshot = { pending: boolean; failed: boolean };
export function createDialogActionScope() {
  let snapshot: Snapshot = { pending: false, failed: false };
  const listeners = new Set<() => void>();
  const busy = new Set<string>();
  let running = 0;
  const publish = (failed = snapshot.failed) => {
    const pending = running > 0 || busy.size > 0;
    if (pending === snapshot.pending && failed === snapshot.failed) return;
    snapshot = { pending, failed };
    listeners.forEach(listener => listener());
  };
  return {
    subscribe: (listener: () => void) => { listeners.add(listener); return () => { listeners.delete(listener); }; },
    getSnapshot: () => snapshot,
    setBusy: (key: string, active: boolean) => { if (active) busy.add(key); else busy.delete(key); publish(); },
    clearFailure: () => publish(false),
    /** Eingabeprüfung findet im Form vor diesem Aufruf statt; Inhalte werden nicht verworfen. */
    invoke: (action: () => unknown): unknown => {
      if (running > 0 || busy.size > 0) return undefined;
      publish(false);
      let result: unknown;
      try { result = action(); }
      catch { publish(true); return undefined; }
      if (result && typeof (result as PromiseLike<unknown>).then === "function") {
        running++;
        publish();
        void Promise.resolve(result).then(
          () => { running--; publish(); },
          () => { running--; publish(true); },
        );
      }
      return result;
    },
  };
}

export type DialogActionScope = ReturnType<typeof createDialogActionScope>;
export const DialogActionContext = createContext<DialogActionScope | null>(null);
export const useDialogAction = () => useContext(DialogActionContext);

export function useDialogActionScope(open: boolean) {
  const scope = useMemo(createDialogActionScope, []);
  const state = useSyncExternalStore(scope.subscribe, scope.getSnapshot, scope.getSnapshot);
  useEffect(() => { if (!open) scope.clearFailure(); }, [open, scope]);
  return { scope, ...state };
}

/** Tatsächliche Verarbeitung, kein Formular-Lader und kein Doppelklickriegel. */
export function useDialogActionBusy(active: boolean) {
  const scope = useDialogAction();
  const key = useId();
  useLayoutEffect(() => {
    if (!scope) return;
    scope.setBusy(key, active);
    return () => scope.setBusy(key, false);
  }, [active, key, scope]);
}
